import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {NucleotideType, PolymerType, ResidueType} from 'biojs/BioConstants';
import {ActionOrigin, MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {
  LONG_SEQUENCE_NUCLEOTIDE_TYPE_TO_POLYMER_SUBTYPE,
  OLIGO_NUCLEOTIDE_TYPE_TO_POLYMER_SUBTYPE,
  POLYMER_SUBTYPE_TO_MODEL_TYPE,
  POLYMER_SUBTYPE_TO_POLYMER_TYPE,
  PolymerSubtype,
} from 'constants/MolbioConstants';
import {SequenceTopology} from 'constants/SequenceConstants';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {ConcatenationBanner} from 'molbio/bulk-assembly/Banners';
import {
  BulkAssemblyMethod,
  ConcatenationMethodData,
  FragmentProductionMethod,
  SEQUENCE_TOPOLOGY_TO_BULK_ASSEMBLY_TOPOLOGY,
  SettingsModalBulkAssemblyParams,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {BinsCount} from 'molbio/bulk-assembly/setup-modal/controls/BinsCount';
import {createEmptyFragmentBins, makeProcedureBinFields} from 'molbio/bulk-assembly/util/BulkAssemblyUtil';
import {TopologyDropdown} from 'molbio/TopologyDropdown';
import StudySearchInput, {StudySearchItem} from 'studies/StudySearchInput';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import ToggleGroup, {ToggleGroupOption} from 'ui-library/ToggleGroup';
import {typeToName} from 'util/NameUtil';

export interface ConcatenationSetupModalProps {
  initialFolderId?: string;
  initialName?: string;
  initialNumFragmentBins?: number;
  initialTopology?: SequenceTopology;
  isInitialSetup: boolean;
  canEditAssembly: boolean;
  canEditFolder: boolean;
  canEditName: boolean;
  initialMethodData?: ConcatenationMethodData;
  actionOrigin: ActionOrigin;
  onRequestClose: () => void;
  onRequestSubmit: (newAssemblyParams: SettingsModalBulkAssemblyParams) => void;
}

/**
 * A modal to choose or update concatenation bulk assembly parameters.
 *
 * @param {CloningSetupModalProps} props Component props
 * @param {string} [props.initialFolderId] The initial folder ID to populate the modal with
 * @param {string}  props.initialName The initial assemblyName to populate when editing an existing assembly settings
 * @param {number} props.initialNumFragmentBins The number of fragment bins to initially populate the dropdown with
 * @param {SequenceTopology} props.initialTopology The initial construct topology to populate the modal with
 * @param {boolean} props.isInitialSetup Whether this modal is being shown as part of initial assembly setup or while editing assembly settings after an assembly has been created
 * @param {boolean} props.canEditAssembly Whether the assembly can be edited, which is not permitted after finalization
 * @param {boolean} props.canEditFolder Whether the user can edit the assembly folder, which is permitted after finalization
 * @param {boolean} props.canEditName Whether the user can edit the assembly name, which is permitted after finalization
 * @param {ConcatenationMethodData} [props.initialMethodData] The initial method parameters to populate the modal with
 * @param {ActionOrigin} props.actionOrigin The place from which the modal was launched, for analytics purposes
 * @param {Function} props.onRequestClose Handler for closing the modal without submitting
 * @param {Function} props.onRequestSubmit Handler for submitting the modal
 * @constructor
 */
export function ConcatenationSetupModal(props: ConcatenationSetupModalProps): JSX.Element {
  const [name, setName] = useState(props.initialName ?? 'Untitled concatenation');
  const [folderId, setFolderId] = useState<string | null>(
    props.initialFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY) ?? null
  );
  const [studySearchItems, setStudySearchItems] = useState<Array<StudySearchItem>>([]);
  const [numFragmentBins, setNumFragmentBins] = useState<number | null>(props.initialNumFragmentBins ?? 2);
  const [isNumFragmentBinsValid, setIsNumFragmentBinsValid] = useState(true);
  const [topology, setTopology] = useState(props.initialTopology ?? SequenceTopology.LINEAR);
  const [constructType, setConstructType] = useState<PolymerSubtype>(
    props.initialMethodData?.parameters?.polymerSubtype ?? PolymerSubtype.dna_sequence
  );
  const [polymerType, setPolymerType] = useState<PolymerType>(POLYMER_SUBTYPE_TO_POLYMER_TYPE[constructType]);
  const [nullifyPhosphates, setNullifyPhosphates] = useState(
    props.initialMethodData?.parameters?.applyDefaultNullTerminalPhosphate ?? true
  );

  let derivedFragmentTypes: Array<PolymerSubtype>;
  let availableConstructTypes: Array<PolymerSubtype>;
  if (polymerType === ResidueType.AA) {
    availableConstructTypes = [PolymerSubtype.aa_sequence];
    derivedFragmentTypes = [PolymerSubtype.aa_sequence];
  } else {
    availableConstructTypes = [
      LONG_SEQUENCE_NUCLEOTIDE_TYPE_TO_POLYMER_SUBTYPE[polymerType],
      OLIGO_NUCLEOTIDE_TYPE_TO_POLYMER_SUBTYPE[polymerType],
    ];
    if (constructType === PolymerSubtype.dna_oligo || constructType === PolymerSubtype.rna_oligo) {
      derivedFragmentTypes = [constructType];
    } else {
      derivedFragmentTypes = [constructType, OLIGO_NUCLEOTIDE_TYPE_TO_POLYMER_SUBTYPE[polymerType]];
    }
  }

  useEffect(() => {
    if (polymerType === ResidueType.AA) {
      if (constructType !== PolymerSubtype.aa_sequence) {
        setConstructType(PolymerSubtype.aa_sequence);
      }
    } else if (
      (constructType === PolymerSubtype.rna_oligo || constructType === PolymerSubtype.dna_oligo) &&
      constructType !== OLIGO_NUCLEOTIDE_TYPE_TO_POLYMER_SUBTYPE[polymerType]
    ) {
      setConstructType(OLIGO_NUCLEOTIDE_TYPE_TO_POLYMER_SUBTYPE[polymerType]);
    } else if (
      (constructType === PolymerSubtype.dna_sequence || constructType === PolymerSubtype.rna_sequence) &&
      LONG_SEQUENCE_NUCLEOTIDE_TYPE_TO_POLYMER_SUBTYPE[polymerType] !== constructType
    ) {
      setConstructType(LONG_SEQUENCE_NUCLEOTIDE_TYPE_TO_POLYMER_SUBTYPE[polymerType]);
    } else if (constructType === PolymerSubtype.aa_sequence) {
      setConstructType(LONG_SEQUENCE_NUCLEOTIDE_TYPE_TO_POLYMER_SUBTYPE[polymerType]);
    }
  }, [constructType, polymerType]);

  useEffect(() => {
    if (constructType !== PolymerSubtype.dna_sequence) {
      setTopology(SequenceTopology.LINEAR);
    }
  }, [constructType]);

  useEffect(() => {
    analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.OPENED_BULK_ASSEMBLY_SETUP_MODAL, {
      actionOrigin: props.actionOrigin,
      cloningMethod: BulkAssemblyMethod.CONCATENATION,
    });
  }, [props.actionOrigin]);

  const sequenceToggleOptions: Array<ToggleGroupOption<PolymerType>> = [
    {
      id: NucleotideType.DNA,
      dataTestAttrs: {component: 'SequenceTypeToggle', element: 'ToggleGroup', key: 'DNA'},
      buttonProps: {text: 'DNA'},
    },
    {
      id: NucleotideType.RNA,
      dataTestAttrs: {component: 'SequenceTypeToggle', element: 'ToggleGroup', key: 'RNA'},
      buttonProps: {text: 'RNA'},
    },
    {
      id: ResidueType.AA,
      dataTestAttrs: {component: 'SequenceTypeToggle', element: 'ToggleGroup', key: 'AA'},
      buttonProps: {text: 'AA'},
    },
  ];
  return (
    <Modal
      title={props.isInitialSetup ? 'Concatenate sequences' : 'Concatenation settings'}
      buttons={[
        {
          text: 'Cancel',
          cancel: true,
        },
        {
          text: 'Save',
          submit: true,
          disabled: name.trim() === '' || folderId == null || !isNumFragmentBinsValid,
          style: ButtonStyle.SECONDARY,
        },
      ]}
      onRequestClose={() => {
        analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.CLOSED_REDESIGNED_BULK_ASSEMBLY_MODAL, {
          actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL,
        });
        props.onRequestClose();
      }}
      onRequestSubmit={() => {
        props.onRequestSubmit({
          name,
          folderId: folderId!,
          procedureJson: {
            method: {
              type: BulkAssemblyMethod.CONCATENATION,
              parameters: {
                polymerSubtype: constructType,
                defaultFragmentProductionMethod: FragmentProductionMethod.SLICE,
                applyDefaultNullTerminalPhosphate: nullifyPhosphates,
              },
            },
            topology: SEQUENCE_TOPOLOGY_TO_BULK_ASSEMBLY_TOPOLOGY[topology],
            ...makeProcedureBinFields(
              createEmptyFragmentBins(
                numFragmentBins!,
                FragmentProductionMethod.SLICE,
                topology,
                BulkAssemblyMethod.CONCATENATION
              )
            ),
            fragments: [],
            constructs: [],
          },
          studyIds: studySearchItems.map(({id}) => id),
        });
      }}
    >
      <TextField
        value={name}
        placeholder="Untitled concatenation"
        label="Name"
        labelStyle={LabelStyle.LABEL}
        onBlur={() => {
          analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_NAME_FIELD, {
            actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL,
            cloningMethod: BulkAssemblyMethod.CONCATENATION,
          });
        }}
        onChange={(e) => {
          setName(e.target.value);
        }}
        isRequired={true}
        dataTestAttrs={{component: 'ConcatenationSetupModal', element: 'nameInput'}}
        disabled={!props.canEditName}
      />
      <div className={css(styles.margin)}>
        <FormGroup>
          <FieldLabel label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} isRequired={true} />
          <FolderSelector
            enableRedesign={true}
            showInventory={true}
            value={folderId}
            onChange={(e) => {
              setFolderId(e.target.value);
            }}
            unsetOnFetchFail={false}
            readOnly={!props.canEditFolder}
            checkExistingItemForDataModsMovement={!props.isInitialSetup}
          />
        </FormGroup>
      </div>
      {/** There is some complexity in editing Studies, so only show the input on the initial setup. Users can later edit the associated
           Studies from the Metadata tab. */}
      {BenchlingConfig.STUDIES_ENABLE && props.isInitialSetup && (
        <div className={css(styles.margin)}>
          <FormGroup>
            <FieldLabel label="Studies" />
            <StudySearchInput
              onChange={(studySearchItems: Array<StudySearchItem>) => {
                setStudySearchItems(studySearchItems);
              }}
              shouldAllowMultiple={true}
              selectedItems={studySearchItems}
            />
          </FormGroup>
        </div>
      )}
      <FormGroup>
        <FieldLabel label="Sequence type" isRequired={true} />
        <ToggleGroup
          options={sequenceToggleOptions}
          selection={polymerType}
          onChangeSelection={(newPolymerType) => {
            analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_NUCLEOTIDE_TYPE, {
              actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL,
              cloningMethod: BulkAssemblyMethod.CONCATENATION,
            });
            setPolymerType(newPolymerType);
          }}
          size={ButtonSize.SMALL}
          isFullWidth={true}
          disabled={!props.isInitialSetup}
        />
      </FormGroup>
      <ConcatenationBanner polymerType={polymerType} />
      <div className={css(styles.flex, styles.margin)}>
        <div className={css(styles.column)}>
          <FormGroup>
            <FieldLabel label="Fragment type(s)" />
            <SelectDropdown
              displayName={derivedFragmentTypes
                .map((polymerSubtype) => typeToName(POLYMER_SUBTYPE_TO_MODEL_TYPE[polymerSubtype]))
                .join(', ')}
              disabled={true}
              menu={<Menu value={0} />}
            />
          </FormGroup>
        </div>
        <div className={css(styles.column)}>
          <FormGroup>
            <FieldLabel label="Construct type" />
            <SelectDropdown
              displayName={typeToName(POLYMER_SUBTYPE_TO_MODEL_TYPE[constructType])}
              disabled={!props.canEditAssembly || availableConstructTypes.length < 2}
              menu={
                <Menu value={constructType} width={MenuWidth.FULL}>
                  {availableConstructTypes.map((availableConstructType) => (
                    <Option
                      key={availableConstructType}
                      value={availableConstructType}
                      onSelect={() => {
                        analytics.trackEvent(
                          MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_OUTPUT_SEQUENCE_TYPE,
                          {
                            actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL,
                            cloningMethod: BulkAssemblyMethod.CONCATENATION,
                          }
                        );
                        setConstructType(availableConstructType);
                      }}
                    >
                      {typeToName(POLYMER_SUBTYPE_TO_MODEL_TYPE[availableConstructType])}
                    </Option>
                  ))}
                </Menu>
              }
            />
          </FormGroup>
        </div>
      </div>
      <div className={css(styles.flex, styles.margin)}>
        <div className={css(styles.column)}>
          <BinsCount
            disabled={!props.isInitialSetup}
            value={numFragmentBins}
            onChangeValue={({isValid, value}) => {
              setNumFragmentBins(value);
              setIsNumFragmentBinsValid(isValid);
            }}
          />
        </div>
        <div className={css(styles.column)}>
          <TopologyDropdown
            label="Topology of construct"
            topology={topology}
            onChange={(sequenceTopology) => {
              analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_TOPOLOGY, {
                actionOrigin: ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL,
                cloningMethod: BulkAssemblyMethod.CONCATENATION,
              });
              setTopology(sequenceTopology);
            }}
            disabled={!props.canEditAssembly || constructType !== PolymerSubtype.dna_sequence}
            disabledTooltipText={props.canEditAssembly ? 'Only DNA sequences can be circular.' : undefined}
          />
        </div>
      </div>
      {(constructType === PolymerSubtype.dna_oligo || constructType === PolymerSubtype.rna_oligo) && (
        <div className={css(styles.flex, styles.margin)}>
          <div className={css(styles.column)}>
            <CheckboxInput
              checked={nullifyPhosphates}
              onChange={(e) => {
                setNullifyPhosphates(e.target.checked);
              }}
              label="Nullify phosphate at 3’ end of all constructs"
              id="null-phosphate-checkbox"
            />
          </div>
        </div>
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  flex: {
    display: 'flex',
    columnGap: '22px',
  },
  column: {
    flex: 1,
  },
  margin: {
    marginTop: '16px',
  },
});
